/**
 * V2 Grid Types
 *
 * Type definitions for the UnifiedGridManager (UGM) v2 data model.
 * Grids are identity-first containers for one or more yield-generating assets.
 */

import type { Address, BigIntString } from './index';

/**
 * Creator-editable presentation for a single yield asset
 * (`pool_metadata.adapter_notes[id]`).
 *
 * Per-asset X URLs are intentionally absent — the board-level
 * OAuth-verified `twitterHandle` is the only sanctioned X surface in
 * the lobby. Existing rows in the DB may still carry a stray
 * `twitterUrl` key from earlier writes; that field is no longer typed,
 * read, written, or rendered by the app.
 */
export interface GridAdapterNote {
  title?: string;
  description?: string;
  imageUrl?: string;
  telegramUrl?: string;
  discordUrl?: string;
  websiteUrl?: string;
}

export interface V2Grid {
  id: string;
  creator: Address;
  totalSeats: number;
  taxRateBps: number;
  taxToken: Address;
  taxTokenDecimals?: number;
  yieldToken: Address;
  yieldTokenDecimals: number;
  graduated: boolean;
  paused: boolean;
  deprecated: boolean;
  managerAddress: Address;
  activeSeatCount: number;
  seatsSoldFromCreator: number;
  uniqueHolderCount: number;
  takeoverCost: BigIntString;
  floorPrice: BigIntString;
  averagePrice: BigIntString;
  ceilingPrice: BigIntString;
  firstSeatClaimedAt: number | null;
  lastSeatPurchasedAt: number | null;
  totalYieldReceived: BigIntString;
  totalTaxCollected: BigIntString;
  totalTaxWithdrawn: BigIntString;
  pendingTaxRevenue: BigIntString;
  totalYieldClaimed: BigIntString;
  estimatedPendingRewards: BigIntString;
  createdAt: number;
  /** Sum of all active asset tvlUsd, 6 decimals */
  totalTvlUsd?: BigIntString;
  /** Sum of all active asset tvlNative, 18 decimals */
  totalTvlNative?: BigIntString;
  // Grid identity (set at creation)
  name?: string;
  imageUrl?: string;
  /** From `pool_metadata.description` */
  description?: string;
  /**
   * From `pool_metadata.board_skybox` — wizard tri-state stored verbatim:
   *   - `'none'` sentinel → creator picked None
   *   - registered theme id (`'aurora'`, `'sci-fi'`, …) → use it
   *   - `undefined` → no choice yet (legacy / pre-wizard board)
   *
   * The client-side coercion lives next to consumers so renderers
   * can decide how to fall back (e.g. adapter-derived default in
   * `gridSummaryToMockBoard`, neutral void in `LobbyClient`'s
   * activeBoard merge for unknown ids).
   */
  boardSkybox?: string;
  /** From `pool_metadata.board_plinth` — same contract as `boardSkybox`. */
  boardPlinth?: string;
  /** Indexer board link (UGM v2.3) */
  boardId?: string;
  /** Shard ordering within a board (UGM v2.3) */
  boardShardIndex?: number;
  /**
   * Number of seats whose initial price has been written via
   * `initializeSeats` on V21. Equal to `totalSeats` for fully-initialized
   * grids and any grid created through a manager that doesn't have the
   * separate-init concept (V2 base, V22 lazy materialization).
   *
   * When strictly less than `totalSeats`, the grid is in the
   * "soft-bricked" partial-init state: `addBatch` (the seat-acquire path)
   * reverts with `GridNotFullyInitialized()`, so the lobby surfaces a
   * "board still initializing" banner and disables buy CTAs until the
   * remaining batches confirm. The server-side route at
   * `/api/grid/[ugm]/[gridId]` reads this via multicall and defaults to
   * `totalSeats` on RPC failure so a transient blip never false-triggers
   * the warning.
   */
  seatsInitialized?: number;
  /**
   * Set when the grid appears in the `banned_grids` table (admin
   * moderation). The API returns banned rows with `banned: true`
   * stamped on them — consumers handle the UX themselves. The
   * takeover-app's lobby filters these client-side in
   * `useLobbyBoards`; the per-board page still resolves so seat
   * holders can manage positions, and renders a moderator warning
   * banner. Stamped outside the per-grid response cache so admin
   * toggles flip live.
   */
  banned?: boolean;

  // ── UGM v2.2 (lean fork) — null/undefined on v2.1 grids ──────────────
  /**
   * `"v2.1"` for legacy UGMs (V20 standalone, V21 hvTAKEOVER) or
   * `"v2.2"` for the lean fork (lazy seats + Dutch auction). Set by the
   * indexer at `GridCreated` from a V22 address allowlist; mirrored in
   * the app via `isV22Grid()`. Drives all version-gated behaviour.
   */
  gridVersion?: 'v2.1' | 'v2.2' | string;
  /**
   * V2.2 only — Dutch auction decay window in seconds. Replaces V2.1's
   * 5-minute anti-snipe waterfall: a forfeited seat's clearing price
   * decays linearly to zero across this window, and the buyer must
   * provide `newDeposit > 0`.
   */
  forfeitureDuration?: number;
  /**
   * V2.2 only — number of seats whose `_seats` row has been written
   * on-chain. Surrogate of "real" V21 `seatsInitialized`. Below
   * `totalSeats` is normal (most seats stay lazy until interacted with).
   */
  seatsMaterialized?: number;
  /**
   * Per-grid governance hook module address (V21 `IGridGovernanceHooks`
   * surface, preserved on V22). Cached by the indexer from
   * `GridGovernanceModuleSet`. Empty/null = no module attached. Used by
   * `/api/grid/[ugm]/[gridId]` to short-circuit per-grid governance reads.
   */
  governanceModule?: string | null;
  /**
   * V2.2 only — comma-separated decimal strings of the per-seat initial
   * price vector emitted at `GridPricesInitialized`. Used for client-side
   * lazy-seat synthesis: unmaterialised seats are rendered as creator-held
   * with this price until the first lifecycle event materialises them.
   * See `synthesizeUnmaterializedSeats` in `use-grid-page.helpers.ts`.
   */
  initialPricesPacked?: string;
}

export interface V2GridSeat {
  id?: string;
  gridId?: string;
  seatIndex: number;
  holder: Address | null;
  selfAssessedPrice: BigIntString;
  deposit: BigIntString;
  lastTaxTime: number;
  lastPriceChangeTime: number;
  heldSince: number;
  isActive: boolean;
  createdAt: number;
  createdTxHash?: string;
  holderIdentity?: {
    displayName?: string;
    avatarUrl?: string;
    level?: number;
  };
  /** Authoritative on-chain currentBalance from RPC (stringified bigint, may be negative) */
  rpcCurrentBalance?: string;
  /**
   * Unix seconds — when `rpcCurrentBalance` was sampled by the server
   * (multicall against `UGM.currentBalance`). Lets the client apply
   * continuous tax decay between API refreshes so the displayed runway
   * counts down every second instead of freezing at the multicall
   * value. Without this, an 8s Redis cache + 15s React Query interval
   * compound into ~25s of staleness even on healthy seats — and the
   * runway display never visibly ticks.
   */
  rpcCurrentBalanceAt?: number;
  /**
   * V2.2 only — `seats(gridId, seatId).forfeitedAt` sampled with the same
   * multicall pass as live balances. Lets `buildSeatData` paint the Dutch
   * decay window when the indexer hasn't ingested `FORFEITED` yet but the
   * contract already cleared the holder and started `_dutchPrice`.
   */
  rpcForfeitedAt?: number;
  /**
   * V2.2 only — `seats(...).holder` from RPC when sampled alongside
   * {@link rpcForfeitedAt}. When this reads `address(0)` but the indexer
   * row still lists the prior holder, we trust the chain for vacant /
   * Dutch semantics.
   */
  rpcOnChainHolder?: `0x${string}` | null;
  /**
   * V2.2 only — `true` when this row was synthesized client-side from
   * `Grid.initialPricesPacked` because no on-chain interaction has yet
   * triggered `_materializeSeat`. Lets consumers branch on lazy-creator
   * tiles (e.g. tile chips that read "lazy" instead of "creator").
   * Persists only across one render — once an event materialises the
   * seat, the indexer overwrites it with the authoritative row.
   */
  isLazy?: boolean;
  /**
   * v2.3 — seat weight for multi-adapter yield routing (stringified bigint).
   */
  yieldWeight?: string;
  /** Unix seconds when {@link yieldWeight} last changed */
  yieldWeightUpdatedAt?: number;
}

export interface V2GridAsset {
  id: string;
  gridId: string;
  active: boolean;
  metadataResolved: boolean;
  registeredAt: number;
  withdrawnAt: number | null;
  totalYieldReceived: BigIntString;
  yield7d: BigIntString;
  yield7dUpdatedAt: number;
  assetType?: 'flaunch' | 'lp' | 'lp-v4';
  adapterAddress?: string;
  name?: string;
  symbol?: string;
  iconUrl?: string;
  /** @deprecated Use tvlUsd instead */
  tvl?: BigIntString;
  /** USD value, 6 decimals (e.g. 2500000000 = $2,500) */
  tvlUsd?: BigIntString;
  /** ETH value, 18 decimals */
  tvlNative?: BigIntString;
  /** Raw amount of token0 in position */
  tvlToken0?: BigIntString;
  /** Raw amount of token1 in position */
  tvlToken1?: BigIntString;
  token0Decimals?: number;
  token1Decimals?: number;
  /** Unix timestamp of last TVL refresh */
  tvlUpdatedAt?: number;
  /** false if exotic pair (no ETH or USDC side) */
  priceable?: boolean;
  feeTier?: number;
  tickLower?: number;
  tickUpper?: number;
  isFullRange?: boolean;
  platform?: 'uniswap-v3' | 'uniswap-v4' | 'aerodrome' | 'flaunch';
  pairLabel?: string;
  token0Address?: string;
  token1Address?: string;
  token0Symbol?: string;
  token1Symbol?: string;
}

export interface V2FloorPriceSnapshot {
  id: string;
  gridId: string;
  floorPrice: BigIntString;
  averagePrice: BigIntString;
  ceilingPrice: BigIntString;
  activeSeatCount: number;
  takeoverCost: BigIntString;
  timestamp: number;
  blockNumber: number;
}

export type V2GridSeatEventType =
  | 'BOUGHT_VACANT'
  | 'BOUGHT_FROM_CREATOR'
  | 'BOUGHT_OUT'
  | 'ABANDONED'
  | 'FORFEITED'
  | 'PRICE_UPDATED'
  | 'DEPOSIT_ADDED'
  | 'DEPOSIT_WITHDRAWN'
  | 'YIELD_CLAIMED'
  | 'TAX_COLLECTED'
  | 'ANTI_SNIPE_TRIGGERED'
  | 'MIGRATED_OUT'
  | 'MIGRATED_IN';

export interface V2GridSeatEvent {
  id: string;
  seatId?: string;
  gridId: string;
  eventType: V2GridSeatEventType;
  user: Address;
  timestamp: number;
  blockNumber: number;
  txHash: string;
  price?: BigIntString;
  amount?: BigIntString;
  amountUsd?: BigIntString | null;
  newDeposit?: BigIntString;
  oldHolder?: Address;
  newHolder?: Address;
  refund?: BigIntString;
  newPrice?: BigIntString;
  userIdentity?: {
    displayName?: string;
    avatarUrl?: string;
    level?: number;
  };
  oldHolderIdentity?: {
    displayName?: string;
    avatarUrl?: string;
    level?: number;
  };
}

export interface V2GridRewardEvent {
  id: string;
  gridId: string;
  assetHash: string | null;
  adapterType: string;
  payee: string;
  token: string;
  amount: BigIntString;
  amountUsd?: BigIntString | null;
  timestamp: number;
  blockNumber: number;
  txHash: string;
}

export interface V2GridDailyStats {
  id: string;
  gridId: string;
  dayTimestamp: number;
  yieldReceived: BigIntString;
  taxCollected: BigIntString;
  taxWithdrawn: BigIntString;
  seatBuys: number;
  seatAbandons: number;
  seatForfeits: number;
  yieldClaimed: BigIntString;
}

export interface V2GridPageData {
  grid: V2Grid;
  seats: V2GridSeat[];
  assets: V2GridAsset[];
  floorPriceSnapshots: V2FloorPriceSnapshot[];
  recentEvents: V2GridSeatEvent[];
  rewardEvents: V2GridRewardEvent[];
  /** Raw pending yield from adapters (V4/Flaunch), in yield token units */
  pendingAdapterYield?: BigIntString;
  /** Per-adapter pending yield breakdown (capped on V22 grids) */
  pendingYieldAdapters?: Array<{
    adapterAddress: string;
    kind: 'flaunch' | 'lp-v3' | 'lp-v4' | 'other';
    pendingYield: string;
  }>;
  /**
   * TAKEOVER/USD price from the indexer, scaled to 18 decimals per 1 TAKEOVER
   * (divide by 1e18 for a USD float). Null if unavailable.
   */
  takeoverPriceUsdc?: string | null;
  dailyStats: V2GridDailyStats[];
  /** Unclaimed whitelist reservations from WhitelistGovernanceModule */
  reservations?: { seatIndex: number; claimant: string; claimantIdentity?: { displayName?: string; avatarUrl?: string; level?: number } }[];
  /** Unix timestamp when reservations expire (0 = no deadline set) */
  claimDeadline?: number;
  creatorIdentity?: {
    address: string;
    displayName?: string;
    avatarUrl?: string;
    level?: number;
  };
  topHolders: V2TopHolder[];
  socialLinks?: {
    twitterUrl?: string;
    /** OAuth-verified X handle (no `@`). Distinct from `twitterUrl`,
     *  which is the free-form display URL kept for legacy rows. New
     *  metadata writes set both — see `pool_metadata.twitter_handle`
     *  and the metadata route's `isHandleVerified` gate. */
    twitterHandle?: string;
    telegramUrl?: string;
    discordUrl?: string;
    websiteUrl?: string;
  };
  /** Per-asset copy keyed by Envio `GridAsset.id` */
  adapterNotes?: Record<string, GridAdapterNote>;
  /**
   * V1 → V2 migration context — `null` (or omitted) when this grid
   * isn't the destination of an active V1 migration. Stamped by the
   * grid API route from classic's `pool_metadata` lookup
   * (`getMigrationForGrid`), drives both the board-level migration
   * banner and the post-claim "abandon V1" nudge modal in the lobby.
   *
   * Typed loosely as the imported V2MigrationContext shape from
   * `@/lib/api/fetchers/migration` — kept inline here to avoid a
   * circular import between types and the fetcher module.
   */
  migration?: {
    v1PoolId: string;
    v1CoinAddress: string | null;
    v1CoinUrl: string;
    v1CoinSymbol: string;
    untilMs: number;
    rewardsMigrated: boolean;
    /**
     * Tri-state — kept loose here to avoid the circular import on the
     * canonical `V2MigrationContext` definition. See `migration.ts`
     * (`isMigrationAssetMoved`) for resolution semantics: `number`
     * means the V1 rescue is on-chain, `null` means it hasn't fired
     * yet, `undefined` means classic hasn't shipped the field
     * (consumers fall back to the time gate).
     */
    v1TreasuryReclaimedAt: number | null | undefined;
  } | null;
}

export interface V2TopHolder {
  address: Address;
  seatCount: number;
  totalValue: BigIntString;
  displayName?: string;
  avatarUrl?: string;
  level?: number;
  heldSince?: number;
}

export const V2_CONSTANTS = {
  TOTAL_SEATS: 100,
  GRID_SIZE: 10,
  SECONDS_PER_WEEK: 7 * 24 * 60 * 60,
  USDC_DECIMALS: 6,
  TAX_RATE_DENOMINATOR: 10000,
  ANTI_SNIPE_WINDOW: 5 * 60, // 5 minutes, matches contract ANTI_SNIPE_WINDOW
  /**
   * V22 fallback for `Grid.forfeitureDuration` when the per-grid override
   * is 0 — mirrors `UnifiedGridManagerV22.DEFAULT_FORFEITURE_DURATION`
   * (10 minutes). Used by `buildSeatData` to compute V22
   * `antiSnipeEndsAt` (Dutch clearing-price-hits-zero end) when the
   * grid hasn't customised the window. Contract source:
   * `takeover-contracts/src/UnifiedGridManagerV22.sol::DEFAULT_FORFEITURE_DURATION`.
   *
   * Distinct from `use-launch-grid.ts::V22_DEFAULT_FORFEITURE_DURATION`
   * (1_800 / 30 minutes), which is the wizard's chosen *launch-time*
   * default — that's the value the create-grid call passes; this one
   * is the contract's hard-coded fallback when 0 is left on the slot.
   */
  V22_DEFAULT_FORFEITURE_DURATION: 10 * 60,
  /**
   * V22 forfeit penalty in basis points — matches the deploy default
   * (`script/43_DeployUGMv22.s.sol::FORFEITURE_PENALTY_BPS = 500`).
   * Charged on tax-underwater forfeits (NOT on voluntary `abandonSeat`
   * / `abandonBatch`). Read by `manage-drawer` to surface the warning
   * chip on V22 seats with low runway.
   *
   * If a future V22 deploy uses a non-default value, swap this to a
   * live `forfeiturePenaltyBps()` view-call read.
   */
  V22_FORFEIT_PENALTY_BPS_DEFAULT: 500,
} as const;
